import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Chip,
    Paper,
    IconButton,
    Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { getRequest } from "../../Fetch/request";
import Loader from "../Loader/Loader";
import { useAuth } from '../../hooks/useAuth';

const SessionProblems = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [problems, setProblems] = useState([]);
    const [sessionInfo, setSessionInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(10);
    const [totalProblems, setTotalProblems] = useState(0);
    const totalPages = Math.max(1, Math.ceil(totalProblems / rowsPerPage));

    const sessionId = new URLSearchParams(location.search).get('sessionId');

    useEffect(() => {
        if (sessionId) {
            fetchSessionProblems();
        } else {
            setError("Session ID is required");
            setLoading(false);
        }
    }, [sessionId]);

    const fetchSessionProblems = async () => {
        try {
            setLoading(true);
            const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
            
            // Note: We now allow non-authenticated users to view session problems

            const response = await getRequest(
                `${process.env.REACT_APP_URL}/session/problems?userId=${userId}&sessionId=${sessionId}`
            );
            
            if (response && (response.status === "200" || response.status === 200)) {
                const fetchedProblems = response.response?.problems || [];
                const transformedProblems = fetchedProblems.map(problem => ({
                    problemId: problem.problemId || problem.id,
                    title: problem.title || problem.problemTitle,
                    problemRating: problem.problemRating,
                    status: problem.status || 'NOT_ATTEMPTED'
                }));
                setProblems(transformedProblems);
                setTotalProblems(transformedProblems.length);
                setSessionInfo(response.response?.sessionInfo || {});
            } else {
                setError("Failed to fetch session problems");
            }
        } catch (error) {
            console.error('Error fetching session problems:', error);
            setError("Failed to fetch session problems");
        } finally {
            setLoading(false);
        }
    };

    const handleNavigate = (problemId) => {
        navigate(`/problem-statement?problemId=${problemId}`);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleBackToSessions = () => {
        if (!isAuthenticated) {
            alert('Please login to access DSA sessions');
            return;
        }
        navigate('/session-details');
    };

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ marginTop: '20px', padding: '20px' }}>
                <Loader />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl" sx={{ marginTop: '20px', padding: '20px' }}>
                <Typography variant="h6" color="error" textAlign="center">
                    {error}
                </Typography>
                <Button 
                    variant="contained" 
                    onClick={handleBackToSessions}
                    sx={{ marginTop: '20px' }}
                >
                    Back to Sessions
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ marginTop: '20px' }}>
            {/* Header */}
            <Box sx={{ marginBottom: '20px' }}>
                {sessionInfo && sessionInfo.title && (
                    <Typography variant="body1" color="text.secondary">
                        {sessionInfo.title}
                    </Typography>
                )}
                <Button 
                    variant="outlined" 
                    onClick={handleBackToSessions}
                    sx={{ 
                        marginTop: '10px',
                        borderColor: '#008080',
                        color: '#008080',
                        '&:hover': {
                            backgroundColor: '#008080',
                            borderColor: '#008080',
                            color: '#fff',
                        },
                    }}
                >
                    ← Back to Sessions
                </Button>
            </Box>

            <Paper elevation={0} sx={{ borderRadius: '8px', overflow: 'hidden', backgroundColor: 'transparent' }}>
                <TableContainer>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize:'14px', py:0.5, backgroundColor: 'transparent' }}>
                                    Status
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', fontSize:'14px', py:0.5, backgroundColor: 'transparent' }}>Title</TableCell>

                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize:'14px', py:0.5, backgroundColor: 'transparent' }}>
                                    Rating
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={3} sx={{ border: 'none' }}>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'center', 
                                        alignItems: 'center',
                                        minHeight: '150px'
                                    }}>
                                        <Loader />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : error ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 2 }}>
                                    <Typography color="error" variant="body2" sx={{ fontSize: '0.9rem' }}>{error}</Typography>
                                </TableCell>
                            </TableRow>
                        ) : problems.length > 0 ? (
                            problems.map((problem) => (
                            <TableRow key={problem.problemId} hover>
                                {/* Status */}
                                <TableCell align="center" sx={{ py: 0.5 }}>
                                    {problem.status === 'Correct' && (
                                        <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: '1.2rem' }} />
                                    )}
                                    {problem.status !== 'Correct' && problem.status !== 'Not Attempted' && (
                                        <CancelIcon sx={{ color: '#F44336', fontSize: '1.2rem' }} />
                                    )}
                                    {problem.status === 'Not Attempted' && (
                                        <HourglassEmptyIcon sx={{ color: '#666', fontSize: '1.2rem' }} />
                                    )}
                                </TableCell>

                                {/* Title (Clickable Text) */}
                                <TableCell
                                    sx={{
                                        cursor: 'pointer',
                                        color: 'primary.main',
                                        textDecoration: 'none',
                                        fontSize: '0.9rem',
                                        fontWeight: 600,
                                        letterSpacing: '0.01em',
                                        py: 0.5,
                                    }}
                                >
                                    <a
                                        href={`/problem-statement?problemId=${problem.problemId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: 'inherit', textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem', letterSpacing: '0.01em' }}
                                    >
                                        {problem.title}
                                    </a>
                                </TableCell>



                                {/* Problem Rating */}
                                <TableCell align="center" sx={{ py: 0.5 }}>
                                    <Typography variant="caption" sx={{ 
                                        fontSize: '0.8rem',
                                        fontWeight: 'bold',
                                        color: problem.problemRating >= 1600 ? '#F44336' : 
                                               problem.problemRating >= 1500 ? '#FF9800' : '#4CAF50'
                                    }}>
                                        {problem.problemRating ? problem.problemRating : 'N/A'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 2 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>No problems found for this session.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {/* Bottom navigation arrows */}
                <Box sx={{ display:'flex', justifyContent:'flex-end', alignItems:'center', p:0.5, gap:0.5 }}>
                    <IconButton aria-label="previous page" size="small" disabled={page===0} onClick={(e)=>handleChangePage(e,page-1)}>
                        <ArrowBackIosNewIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                    <Typography variant="caption" sx={{ minWidth:50, textAlign:'center', fontSize: '0.8rem' }}>
                        {`${page+1} / ${totalPages}`}
                    </Typography>
                    <IconButton aria-label="next page" size="small" disabled={page >= totalPages-1} onClick={(e)=>handleChangePage(e,page+1)}>
                        <ArrowForwardIosIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                </Box>
            </Paper>
        </Container>
    );
};

export default SessionProblems; 